/**
 * Servicio Cliente de Unidades de Medida y Conversión Dimensional Odoo 18
 * Ruta: src/services/uomService.ts
 */

import {
  UomCategoryKey,
  UomCategory,
  UomDefinition,
  UomConversionResult,
  ItemUomContext
} from '../types/uom';

export const ODOO18_UOM_CATEGORIES: Record<UomCategoryKey, UomCategory> = {
  Unit: {
    id: 'unit',
    name: 'Unidad (Unit)',
    referenceUoM: 'UND',
    uoms: {
      'UND': { code: 'UND', label: 'Unidad (UND)', ratio: 1.0, uomType: 'reference' },
      'PAR': { code: 'PAR', label: 'Par (2 UND)', ratio: 2.0, uomType: 'bigger' },
      'CJ': { code: 'CJ', label: 'Caja (CJ)', ratio: 100.0, uomType: 'bigger', useCantEnBulto: true },
      'PAQ': { code: 'PAQ', label: 'Paquete (PAQ)', ratio: 50.0, uomType: 'bigger', useCantEnBulto: true },
      'KIT': { code: 'KIT', label: 'Kit / Juego', ratio: 1.0, uomType: 'reference' }
    }
  },
  Length: {
    id: 'length',
    name: 'Longitud (Length)',
    referenceUoM: 'ML',
    uoms: {
      'ML': { code: 'ML', label: 'Metro Lineal (ML)', ratio: 1.0, uomType: 'reference' },
      'M': { code: 'M', label: 'Metro (M)', ratio: 1.0, uomType: 'reference' },
      'CM': { code: 'CM', label: 'Centímetro (CM)', ratio: 0.01, uomType: 'smaller' },
      'MM': { code: 'MM', label: 'Milímetro (MM)', ratio: 0.001, uomType: 'smaller' },
      'ROLLO': { code: 'ROLLO', label: 'Rollo (std 50 ML)', ratio: 50.0, uomType: 'bigger' },
      'BARRA': { code: 'BARRA', label: 'Barra / Tubo (std 6 ML)', ratio: 6.0, uomType: 'bigger' }
    }
  },
  Volume: {
    id: 'volume',
    name: 'Volumen (Volume)',
    referenceUoM: 'L',
    uoms: {
      'L': { code: 'L', label: 'Litro (L)', ratio: 1.0, uomType: 'reference' },
      'GAL': { code: 'GAL', label: 'Galón (3.785 L)', ratio: 3.785, uomType: 'bigger' },
      'CUNETE': { code: 'CUNETE', label: 'Cuñete (18.925 L / 5 Gal)', ratio: 18.925, uomType: 'bigger' }
    }
  },
  Area: {
    id: 'area',
    name: 'Superficie (Area)',
    referenceUoM: 'M2',
    uoms: {
      'M2': { code: 'M2', label: 'Metro Cuadrado (M²)', ratio: 1.0, uomType: 'reference' },
      'PLANCHA': { code: 'PLANCHA', label: 'Lámina / Plancha (std 4.465 M²)', ratio: 4.4652, uomType: 'bigger' }
    }
  },
  Weight: {
    id: 'weight',
    name: 'Peso (Weight)',
    referenceUoM: 'KG',
    uoms: {
      'KG': { code: 'KG', label: 'Kilogramo (KG)', ratio: 1.0, uomType: 'reference' },
      'G': { code: 'G', label: 'Gramo (G)', ratio: 0.001, uomType: 'smaller' },
      'TON': { code: 'TON', label: 'Tonelada (TON)', ratio: 1000.0, uomType: 'bigger' }
    }
  }
};

/**
 * Normaliza cualquier variante de UoM al código estándar
 */
export function normalizeUomCode(rawCode?: string | null): string {
  if (!rawCode) return 'UND';
  const clean = rawCode.trim().toUpperCase();
  if (clean === 'UNIDAD' || clean === 'UND') return 'UND';
  if (clean === 'METRO' || clean === 'METRO LINEAL' || clean === 'ML') return 'ML';
  if (clean === 'LITRO' || clean === 'L') return 'L';
  if (clean === 'LÁMINA' || clean === 'LAMINA' || clean === 'PLANCHA') return 'PLANCHA';
  if (clean === 'KILOGRAMO' || clean === 'KG' || clean === 'KILO') return 'KG';
  if (clean === 'GALON' || clean === 'GALÓN' || clean === 'GAL') return 'GAL';
  if (clean === 'CUÑETE' || clean === 'CUNETE') return 'CUNETE';
  if (clean === 'CAJA' || clean === 'CJ') return 'CJ';
  if (clean === 'PAQUETE' || clean === 'PAQ') return 'PAQ';
  if (clean === 'METRO CUADRADO' || clean === 'M2') return 'M2';
  return clean;
}

/**
 * Localiza la categoría y definición de una UoM
 */
export function findUomCategory(uomCode: string): { categoryKey: UomCategoryKey; category: UomCategory; uomDef: UomDefinition } | null {
  const norm = normalizeUomCode(uomCode);
  for (const [key, cat] of Object.entries(ODOO18_UOM_CATEGORIES) as [UomCategoryKey, UomCategory][]) {
    if (cat.uoms[norm]) {
      return {
        categoryKey: key,
        category: cat,
        uomDef: cat.uoms[norm]
      };
    }
  }
  return null;
}

/**
 * Retorna las opciones de UoM compatibles para la compra de un insumo según su UoM base
 */
export function getCompatibleUoMs(baseUomCode: string, itemContext: ItemUomContext = {}): UomDefinition[] {
  const normBase = normalizeUomCode(baseUomCode);
  const found = findUomCategory(normBase);

  if (found) {
    const list = Object.values(found.category.uoms);
    // Si la categoría es Unit y hay cant_en_bulto, personalizar labels de CJ y PAQ
    if (found.categoryKey === 'Unit' && itemContext.cant_en_bulto && itemContext.cant_en_bulto > 0) {
      return list.map(u => {
        if (u.code === 'CJ') {
          return { ...u, label: `Caja (${itemContext.cant_en_bulto} und)` };
        }
        if (u.code === 'PAQ') {
          return { ...u, label: `Paquete (${itemContext.cant_en_bulto} und)` };
        }
        return u;
      });
    }
    return list;
  }

  // Fallback si la UoM no está registrada: retornar base + opciones universales de empaque
  return [
    { code: normBase, label: `${normBase} (Base)`, ratio: 1.0, uomType: 'reference' },
    { code: 'CJ', label: itemContext.cant_en_bulto ? `Caja (${itemContext.cant_en_bulto} ${normBase})` : 'Caja (CJ)', ratio: itemContext.cant_en_bulto || 1.0, uomType: 'bigger', useCantEnBulto: true },
    { code: 'PAQ', label: itemContext.cant_en_bulto ? `Paquete (${itemContext.cant_en_bulto} ${normBase})` : 'Paquete (PAQ)', ratio: itemContext.cant_en_bulto || 1.0, uomType: 'bigger', useCantEnBulto: true }
  ];
}

/**
 * Convierte una cantidad de compra a la UoM base de inventario,
 * calculando el factor real y el costo unitario base derivado.
 */
export function convertUom(
  qty: number,
  fromUom: string,
  toBaseUom: string,
  itemContext: ItemUomContext = {},
  unitPricePurchaseUSD: number = 0
): UomConversionResult {
  const normFrom = normalizeUomCode(fromUom);
  const normTo = normalizeUomCode(toBaseUom);

  if (normFrom === normTo) {
    return {
      targetQty: qty,
      factor: 1.0,
      compatible: true,
      formulaDisplay: `${qty} ${normTo}`,
      unitCostBaseUSD: unitPricePurchaseUSD
    };
  }

  const fromInfo = findUomCategory(normFrom);
  const toInfo = findUomCategory(normTo);

  // Mismo árbol dimensional Odoo 18
  if (fromInfo && toInfo && fromInfo.categoryKey === toInfo.categoryKey) {
    let fromRatio = fromInfo.uomDef.ratio;
    if (fromInfo.uomDef.useCantEnBulto && itemContext.cant_en_bulto && itemContext.cant_en_bulto > 0) {
      fromRatio = itemContext.cant_en_bulto;
    }

    let toRatio = toInfo.uomDef.ratio;
    if (toInfo.uomDef.useCantEnBulto && itemContext.cant_en_bulto && itemContext.cant_en_bulto > 0) {
      toRatio = itemContext.cant_en_bulto;
    }

    const factor = fromRatio / toRatio;
    const targetQty = Math.round(qty * factor * 10000) / 10000;
    const unitCostBaseUSD = factor > 0 && unitPricePurchaseUSD > 0
      ? Math.round((unitPricePurchaseUSD / factor) * 10000) / 10000
      : unitPricePurchaseUSD;

    const formulaDisplay = `${qty} ${normFrom} × ${factor} = ${targetQty} ${normTo}`;

    return {
      targetQty,
      factor,
      compatible: true,
      formulaDisplay,
      unitCostBaseUSD
    };
  }

  // Si origen es empaque y tenemos cant_en_bulto
  if (itemContext.cant_en_bulto && itemContext.cant_en_bulto > 0 && (normFrom === 'CJ' || normFrom === 'PAQ')) {
    const factor = itemContext.cant_en_bulto;
    const targetQty = qty * factor;
    const unitCostBaseUSD = unitPricePurchaseUSD > 0 ? unitPricePurchaseUSD / factor : 0;
    return {
      targetQty,
      factor,
      compatible: true,
      formulaDisplay: `${qty} ${normFrom} × ${factor} = ${targetQty} ${normTo}`,
      unitCostBaseUSD
    };
  }

  // Fallback 1:1
  return {
    targetQty: qty,
    factor: 1.0,
    compatible: false,
    formulaDisplay: `${qty} ${normFrom} (Sin equivalencia directa ➔ 1:1 con ${normTo})`,
    unitCostBaseUSD: unitPricePurchaseUSD
  };
}
